import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IntegrationsService } from 'src/providers/IntegrationsProvider/Integrations.service';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { tAccountErrorCode } from 'src/providers/SocketsProvider/Sockets.types';
import { toAccountErrorCode } from './accountErrorCode';

/**
 * Snapshot of the WhatsApp Web account status for the open workspace.
 * Combines (a) a one-shot REST hydration on mount and (b) live updates from
 * the `workspace_event.account_update` Socket.IO event.
 *
 * The dashboard's "ver detalle de desconexión" modal consumes this hook so
 * the user can see why their account is offline even when they open the app
 * hours after the disconnect happened — without REST hydration the modal
 * would be blank until a fresh event arrives.
 */
export type tWhatsAppWebAccountStatus = {
  connected: boolean | null;
  hasAccount: boolean | null;
  state: number | null;
  errorCode: tAccountErrorCode | null;
  errorMessage: string | null;
  occurredAt: string | null;
  /** True while the mount-time REST fetch is still in flight. */
  isLoading: boolean;
  /** Manual re-fetch trigger; reuses the same race-protected path as mount. */
  refetch: () => void;
};

type tUseWhatsAppWebAccountStatusParams = {
  workspaceId: string | undefined;
};

const EMPTY_STATE: Omit<
  tWhatsAppWebAccountStatus,
  'isLoading' | 'refetch'
> = {
  connected: null,
  hasAccount: null,
  state: null,
  errorCode: null,
  errorMessage: null,
  occurredAt: null,
};

export function useWhatsAppWebAccountStatus(
  params: tUseWhatsAppWebAccountStatusParams,
): tWhatsAppWebAccountStatus {
  const { workspaceId } = params;
  const { workspaceAccountUpdateEvent } = useSocketsContext();

  const [snapshot, setSnapshot] =
    useState<Omit<tWhatsAppWebAccountStatus, 'isLoading' | 'refetch'>>(
      EMPTY_STATE,
    );
  const [isLoading, setIsLoading] = useState(false);

  // Monotonic token: only the latest-dispatched fetch is allowed to commit
  // a snapshot. Mirrors the pattern in useWhatsAppWebConnection so an
  // in-flight mount fetch cannot overwrite a fresher socket event that
  // arrives mid-await.
  const fetchSeqRef = useRef(0);
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  const fetchStatus = useCallback(async (): Promise<void> => {
    const callWorkspaceId = workspaceIdRef.current;
    if (!callWorkspaceId) return;

    const seq = ++fetchSeqRef.current;
    setIsLoading(true);

    try {
      const response = await IntegrationsService.getAccountStatus({
        workspaceId: callWorkspaceId,
      });

      if (
        callWorkspaceId !== workspaceIdRef.current ||
        seq !== fetchSeqRef.current
      ) {
        return;
      }

      const { data } = response;
      if (!data) {
        setSnapshot(EMPTY_STATE);
        return;
      }

      setSnapshot({
        connected: data.connected,
        hasAccount: data.hasAccount,
        state: data.state,
        errorCode: toAccountErrorCode(data.lastDisconnectCode),
        errorMessage: data.lastDisconnectMessage ?? null,
        occurredAt: data.lastDisconnectAt ?? null,
      });
    } catch (error) {
      if (
        callWorkspaceId !== workspaceIdRef.current ||
        seq !== fetchSeqRef.current
      ) {
        return;
      }

      // HTTP 404 means "no account for this workspace" — the endpoint's
      // exception filter surfaces it for ChatDaddy-deleted rows. Treat it
      // as a known state rather than an error.
      const httpStatus = isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (httpStatus === 404) {
        setSnapshot({
          ...EMPTY_STATE,
          connected: false,
          hasAccount: false,
        });
      }
      // For 5xx / network failures we leave the snapshot untouched so a
      // transient blip does not blank a known state.
    } finally {
      if (
        callWorkspaceId === workspaceIdRef.current &&
        seq === fetchSeqRef.current
      ) {
        setIsLoading(false);
      }
    }
  }, []);

  // Mount + workspace-switch fetch.
  useEffect(() => {
    if (!workspaceId) {
      setSnapshot(EMPTY_STATE);
      setIsLoading(false);
      return;
    }
    fetchStatus();
  }, [workspaceId, fetchStatus]);

  // Live socket updates. ahooks/useEventEmitter re-binds callbackRef on every
  // render (see node_modules/ahooks/lib/useEventEmitter/index.js), so the
  // closure always reads the latest `workspaceId`.
  workspaceAccountUpdateEvent.useSubscription((event) => {
    if (!workspaceId || event.workspace_id !== workspaceId) return;
    if (event.integration_type && event.integration_type !== 'whatsapp_web') {
      return;
    }

    setSnapshot((prev) => ({
      ...prev,
      connected: event.connected,
      state: event.state,
      // On reconnect (`connected: true`) the backend nulls
      // `last_disconnect_*`, so clear our local snapshot to match — the modal
      // would otherwise show a stale terminal reason after a successful
      // reconnect within the same session.
      errorCode: event.connected ? null : toAccountErrorCode(event.error_code),
      errorMessage: event.connected ? null : event.error_message ?? null,
      occurredAt: event.connected
        ? null
        : event.error_code
        ? new Date().toISOString()
        : null,
    }));
  });

  return {
    ...snapshot,
    isLoading,
    refetch: fetchStatus,
  };
}
